import { useState } from 'react';
import { Caption, Section, Subheadline, Switch } from '@telegram-apps/telegram-ui';
import { AuthorActionResponse, BoxOwnerView } from '@veylme/shared';

import { apiFetch } from '../api/client.js';
import { ApiAuthError } from '../api/errors.js';
import { useT } from '../hooks/useT.js';
import styles from './BoxPanel.module.css';

export interface BoxPanelProps {
  /** Загруженный ящик автора (POST /boxes). */
  box: BoxOwnerView;
  /** Синхронизация ящика в родителя после тумблера приёма. */
  onBoxChange: (box: BoxOwnerView) => void;
}

/**
 * BoxPanel — тумблер приёма вопросов («Твой ящик») в Настройках. Ссылка для
 * приглашений живёт отдельно наверху вкладки «Вопросы» (InvitePanel).
 */
export function BoxPanel({ box, onBoxChange }: BoxPanelProps) {
  const t = useT();
  const [boxToggleBusy, setBoxToggleBusy] = useState(false);
  const [boxToggleError, setBoxToggleError] = useState<string | null>(null);

  async function handleToggleBox() {
    if (boxToggleBusy) return;
    const next = !box.isActive;
    setBoxToggleBusy(true);
    setBoxToggleError(null);
    try {
      await apiFetch('/boxes/toggle', {
        method: 'POST',
        body: { boxId: box.id, active: next },
        parse: AuthorActionResponse,
      });
      onBoxChange({ ...box, isActive: next });
    } catch (err: unknown) {
      // Протухший initData (долго открытое приложение) → 401: подпись Telegram
      // не обновляется без перезапуска, поэтому просим перезапустить, а не
      // показываем общую ошибку (см. INITDATA_MAX_AGE_SEC на сервере).
      setBoxToggleError(
        err instanceof ApiAuthError ? t.ask_error_restart : t.inbox_toggle_error,
      );
    } finally {
      setBoxToggleBusy(false);
    }
  }

  return (
    <Section
      header={
        <div className={styles.boxHeader}>
          <Subheadline weight="2" Component="span">
            {t.inbox_title}
          </Subheadline>
          <Switch
            checked={box.isActive}
            onChange={handleToggleBox}
            disabled={boxToggleBusy}
          />
        </div>
      }
    >
      {/*
        Дети секции — массивом с .filter(Boolean): кит вставляет Divider ПОСЛЕ
        каждого слота-ребёнка (по Children.count), а пустые false/null от условных
        строк он тоже считает → рисует фантомные линии. Falsy-слоты убираем ДО
        передачи в Section, чтобы разделители стояли только между реальными строками.
      */}
      {[
        !box.isActive ? (
          <div key="disabled" className={styles.sectionRow}>
            <Caption Component="p" className={styles.disabledWarning}>
              {t.inbox_box_disabled_warning}
            </Caption>
          </div>
        ) : null,
        boxToggleError ? (
          <div key="toggleError" className={styles.sectionRow}>
            <Caption Component="p" className={styles.disabledWarning}>
              {boxToggleError}
            </Caption>
          </div>
        ) : null,
      ].filter(Boolean)}
    </Section>
  );
}
